import { useTranslations } from "next-intl";
import { useState } from "react";
import { useReferralProgram } from "@hooks/use-features";
import { useCurrentUser } from "@hooks/use-current-user";
import Button from "components/core/Button";
import Link from "components/core/Link";
import { usePrice } from "@hooks/use-prices";
import redeemTypes from "@utils/constants/redeemTypes";
import ReferralShareLink from "./ReferralShareLink";

function ReferralShare() {
  const tCommon = useTranslations("common");
  const tReferrals = useTranslations("referrals");

  const {
    referree_coupon: referreeCoupon,
    referrer_subscription_credit_reward: referrerRewardInCents,
  } = useReferralProgram();
  const user = useCurrentUser();
  const price = usePrice();

  const [copy, setCopy] = useState(false);

  const durationInMonths = parseInt(referreeCoupon.duration_in_months, 10) || 1;
  const referrerGainAmount = price(referrerRewardInCents, {
    withoutCents: true,
    withoutSpace: true,
  });
  const referreeBenefitAmount = price(referreeCoupon.value, {
    withoutCents: true,
    withoutSpace: true,
  });

  const referreeGainText = () => {
    switch (referreeCoupon.duration) {
      case "forever":
        switch (referreeCoupon.type) {
          case redeemTypes.couponPercentOff:
            return tCommon.rich("referral.referree_gain.percent_off_forever", {
              percent: referreeCoupon.value,
              span: (chunks) => <span className="font-size-xl">{chunks}</span>,
            });
          case redeemTypes.couponAmountOff:
            return tCommon.rich("referral.referree_gain.amount_off_forever", {
              amount: price(referreeCoupon.value, { withoutCents: true, withoutSpace: true }),
              span: (chunks) => <span className="font-size-xl">{chunks}</span>,
            });
        }
        break;
      case "once":
      case "repeating":
        switch (referreeCoupon.type) {
          case redeemTypes.couponPercentOff:
            return tCommon.rich("referral.referree_gain.percent_off", {
              percent: referreeCoupon.value,
              count: durationInMonths,
              span: (chunks) => <span className="font-size-xl">{chunks}</span>,
            });
          case redeemTypes.couponAmountOff:
            return tCommon.rich("referral.referree_gain.amount_off", {
              amount: price(referreeCoupon.value, { withoutCents: true, withoutSpace: true }),
              count: durationInMonths,
              span: (chunks) => <span className="font-size-xl">{chunks}</span>,
            });
        }
    }
  };

  const shareNowText = () => {
    switch (referreeCoupon.duration) {
      case "forever":
        switch (referreeCoupon.type) {
          case redeemTypes.couponPercentOff:
            return tReferrals("share_now.percent_off_forever", {
              referrer_gain_amount: referrerGainAmount,
              percent: referreeCoupon.value,
            });
          case redeemTypes.couponAmountOff:
            return tReferrals("share_now.amount_off_forever", {
              referrer_gain_amount: referrerGainAmount,
              amount: referreeBenefitAmount,
            });
        }
        break;
      case "once":
      case "repeating":
        switch (referreeCoupon.type) {
          case redeemTypes.couponPercentOff:
            return tReferrals("share_now.percent_off", {
              referrer_gain_amount: referrerGainAmount,
              percent: referreeCoupon.value,
              count: durationInMonths,
            });
          case redeemTypes.couponAmountOff:
            return tReferrals("share_now.amount_off", {
              referrer_gain_amount: referrerGainAmount,
              amount: referreeBenefitAmount,
              count: durationInMonths,
            });
        }
    }
  };

  function copyToClipboard() {
    navigator.clipboard.writeText(user.referring_token);

    setCopy(true);
  }

  return (
    <div className="referral-share">
      <div className="referral-img" />

      <div className="panel-main">
        <div className="panel-container">
          <div className="panel-content">
            <div className="text-center">
              <div className="mb-3">
                <h4 className="mb-0">
                  {tCommon.rich("referral.referrer_gain", {
                    amount: price(referrerRewardInCents, {
                      withoutCents: true,
                      withoutSpace: true,
                    }),
                    span: (chunks) => <span className="font-size-xl">{chunks}</span>,
                  })}
                </h4>
                <h4 className="mb-0">{referreeGainText()}</h4>
              </div>

              <div className="pb-4 mb-2">
                <p className="mb-0">{shareNowText()}</p>
              </div>

              {user.referring_token ? (
                <div>
                  <ReferralShareLink trackingName="footerShareReferral" />

                  <p className="mb-2 mt-4 text-center">{tReferrals("share_code_description")}</p>
                  <Button
                    rightIconName="copy-icon"
                    onClick={copyToClipboard}
                    variant="ghost-primary"
                    className="bg-white text-uppercase"
                    fullWidth
                  >
                    {user.referring_token}
                  </Button>

                  {copy && (
                    <p className="font-size-xs font-italic mb-0 text-center text-primary">
                      {tReferrals("code_copied")}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="mb-2">{tReferrals("subscriber_only")}</p>
                  <Link href="/je-commence">
                    <Button>{tCommon("referral.subscribe")}</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferralShare;
